import React from 'react';
import {
  Cloud,
  CloudOff,
  Save,
  Download,
  RefreshCw,
  User,
  Calendar,
  Database,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Settings
} from 'lucide-react';
import { useFoodContext } from '../context';
import { useNetworkStatus } from '../hooks';
import { Button } from '../components';
import { formatDate } from '../utils/helpers';

/**
 * Google Drive Status Information Component
 */
const GoogleDriveStatusInfo = ({ googleDrive }) => {
  // Don't show if connected
  if (googleDrive.isConnected) {
    return null;
  }

  // Don't show if initializing
  if (googleDrive.isInitializing) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-700">Initialisation de Google Drive...</span>
        </div>
      </div>
    );
  }

  // Show error if any
  if (googleDrive.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-medium">Erreur de synchronisation</p>
            <p className="text-xs text-red-600 mt-1">{googleDrive.error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show info about local mode
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-amber-700 font-medium">Mode local actif</p>
          <p className="text-xs text-amber-600 mt-1">
            Vos données sont sauvegardées localement. Connectez-vous à Google Drive pour la synchronisation entre appareils.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Sync Page Component - Google Drive synchronization management
 */
const SyncPage = () => {
  const {
    googleDrive,
    getAppStats,
    resetApplication
  } = useFoodContext();

  const isOnline = useNetworkStatus();
  const stats = getAppStats();

  const handleConnect = async () => {
    try {
      await googleDrive.connect();
    } catch (error) {
      console.error('Connection failed:', error);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir vous déconnecter de Google Drive ? ' +
      'Vos données locales seront conservées mais ne seront plus synchronisées.'
    );
    
    if (confirmed) {
      await googleDrive.disconnect();
    }
  };

  const handleManualSync = async () => {
    try {
      await googleDrive.manualSync();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  };

  const handleRestore = async () => {
    try {
      const data = await googleDrive.restoreData();
      if (data) {
        window.location.reload(); // Reload to apply restored data
      }
    } catch (error) {
      console.error('Restore failed:', error);
    }
  };

  const handleReset = () => {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir réinitialiser toutes les données ? ' +
      'Cette action supprimera tous vos aliments personnalisés et ' +
      'remettra l\'application à son état initial. Cette action est irréversible.'
    );
    
    if (confirmed) {
      const doubleConfirm = window.confirm(
        'DERNIÈRE CONFIRMATION : Toutes vos données seront perdues. ' +
        'Voulez-vous vraiment continuer ?'
      );
      
      if (doubleConfirm) {
        resetApplication();
      }
    }
  };

  const renderConnectionStatus = () => {
    if (googleDrive.isInitializing) {
      return (
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center justify-center gap-3">
            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
            <span className="text-blue-800 font-medium">Initialisation de Google Drive...</span>
          </div>
        </div>
      );
    }

    if (googleDrive.error) {
      return (
        <div className="bg-red-50 rounded-xl p-6 border border-red-200">
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 mb-2">Erreur de synchronisation</h3>
              <p className="text-sm text-red-700 mb-4">{googleDrive.error}</p>
              <div className="flex gap-2">
                <Button
                  onClick={handleConnect}
                  size="sm"
                  variant="primary"
                  disabled={!isOnline}
                  title={!isOnline ? 'Indisponible hors ligne' : ''}
                >
                  Réessayer la connexion
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  size="sm"
                  variant="secondary"
                >
                  Recharger la page
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!googleDrive.isConnected) {
      return (
        <div className="text-center py-8">
          <Cloud className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-600 mb-4">
            Connectez votre Google Drive
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Sauvegardez automatiquement vos données et synchronisez-les entre tous vos appareils.
          </p>
          
          <div className="bg-blue-50 rounded-lg p-4 mb-6 max-w-md mx-auto">
            <h4 className="font-medium text-blue-800 mb-2">Avantages de la synchronisation :</h4>
            <ul className="text-sm text-blue-700 text-left space-y-1">
              <li>• Sauvegarde automatique de vos aliments personnalisés</li>
              <li>• Accès depuis tous vos appareils</li>
              <li>• Protection contre la perte de données</li>
              <li>• Synchronisation en temps réel</li>
            </ul>
          </div>
          
          <Button
            onClick={handleConnect}
            size="lg"
            disabled={!isOnline || googleDrive.isSyncing}
            loading={googleDrive.isSyncing}
            title={!isOnline ? 'Indisponible hors ligne' : ''}
          >
            <Cloud className="w-5 h-5" />
            Se connecter à Google Drive
          </Button>
        </div>
      );
    }

    return (
      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-green-800 flex items-center gap-2">
                <User className="w-4 h-4" />
                Connecté à Google Drive
              </h3>
              {googleDrive.googleUser && (
                <p className="text-sm text-green-600">
                  Compte: {googleDrive.googleUser.getName()}
                </p>
              )}
              {googleDrive.lastSyncTime && (
                <p className="text-xs text-green-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Dernière sync: {formatDate(googleDrive.lastSyncTime)}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={handleDisconnect}
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-800 border-red-300 hover:border-red-400"
          >
            Déconnecter
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <Cloud className="w-6 h-6 text-green-600" />
          Synchronisation Google Drive
        </h2>

        {/* Connection Status */}
        <GoogleDriveStatusInfo googleDrive={googleDrive} />
        
        {renderConnectionStatus()}

        {/* Sync Controls - Only show when connected */}
        {googleDrive.isConnected && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {/* Save Section */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Sauvegarde
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Sauvegarde vos données actuelles sur Google Drive
                </p>
                <Button
                  onClick={handleManualSync}
                  disabled={!isOnline || googleDrive.isSyncing}
                  loading={googleDrive.isSyncing}
                  variant="primary"
                  size="sm"
                  className="w-full"
                  title={!isOnline ? 'Indisponible hors ligne' : ''}
                >
                  {googleDrive.isSyncing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Synchronisation...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Synchroniser maintenant
                    </>
                  )}
                </Button>
              </div>

              {/* Restore Section */}
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Restauration
                </h4>
                <p className="text-sm text-orange-700 mb-3">
                  Restaure vos données depuis Google Drive
                </p>
                <Button
                  onClick={handleRestore}
                  disabled={!isOnline || googleDrive.isSyncing}
                  variant="warning"
                  size="sm"
                  className="w-full"
                  title={!isOnline ? 'Indisponible hors ligne' : ''}
                >
                  <Download className="w-4 h-4" />
                  Restaurer les données
                </Button>
              </div>
            </div>

            {/* Sync Status */}
            {googleDrive.isSyncing && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mt-6">
                <div className="flex items-center justify-center gap-3">
                  <CloudOff className="w-5 h-5 text-blue-600 animate-pulse" />
                  <span className="text-blue-800 font-medium">Synchronisation en cours...</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* App Statistics */}
        <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Statistiques de l'application
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-gray-600">Aliments total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.added}</div>
              <div className="text-sm text-gray-600">Aliments ajoutés</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.selected}</div>
              <div className="text-sm text-gray-600">Dans le repas</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {googleDrive.isConnected ? '✓' : '✗'}
              </div>
              <div className="text-sm text-gray-600">Synchronisé</div>
            </div>
          </div>

          {/* Sources breakdown */}
          <div className="mt-6 pt-4 border-t border-gray-300">
            <h4 className="font-medium text-gray-700 mb-3">Répartition par source :</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.sources).map(([source, count]) => (
                <span key={source} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {source}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="mt-8 bg-yellow-50 rounded-xl p-6 border border-yellow-200">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuration de la synchronisation
          </h3>
          
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-yellow-300">
              <h4 className="font-medium text-yellow-800 mb-2">🔄 Synchronisation automatique</h4>
              <p className="text-sm text-yellow-700 mb-2">
                Vos données sont automatiquement sauvegardées 3 secondes après chaque modification.
              </p>
              <p className="text-xs text-yellow-600">
                La synchronisation se fait uniquement quand vous êtes connecté à Google Drive.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-yellow-300">
              <h4 className="font-medium text-yellow-800 mb-2">📁 Stockage des données</h4>
              <p className="text-sm text-yellow-700 mb-2">
                Vos données sont stockées dans le dossier application de votre Google Drive.
              </p>
              <p className="text-xs text-yellow-600">
                Le fichier de sauvegarde n'est visible que par cette application.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-yellow-300">
              <h4 className="font-medium text-yellow-800 mb-2">🔒 Sécurité et confidentialité</h4>
              <p className="text-sm text-yellow-700 mb-2">
                Cette application n'accède qu'aux fichiers qu'elle a créés.
              </p>
              <p className="text-xs text-yellow-600">
                Aucune autre donnée de votre Google Drive n'est accessible.
              </p>
            </div>
          </div>
        </div>

        {/* Troubleshooting Section */}
        {googleDrive.error && (
          <div className="mt-8 bg-red-50 rounded-xl p-6 border border-red-200">
            <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Résolution des problèmes
            </h3>
            
            <div className="space-y-3">
              <div className="bg-white p-3 rounded border border-red-300">
                <h4 className="font-medium text-red-800 text-sm mb-1">🚫 Erreur de connexion</h4>
                <p className="text-xs text-red-700 mb-2">
                  Si la connexion échoue, essayez ces solutions :
                </p>
                <ul className="text-xs text-red-600 space-y-1">
                  <li>• Autorisez les pop-ups pour ce site</li>
                  <li>• Vérifiez votre connexion internet</li>
                  <li>• Désactivez temporairement votre bloqueur de publicités</li>
                  <li>• Essayez dans un onglet de navigation privée</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border border-red-300">
                <h4 className="font-medium text-red-800 text-sm mb-1">🔄 Problème de synchronisation</h4>
                <p className="text-xs text-red-700 mb-2">
                  Si la synchronisation ne fonctionne pas :
                </p>
                <ul className="text-xs text-red-600 space-y-1">
                  <li>• Déconnectez-vous et reconnectez-vous</li>
                  <li>• Vérifiez l'espace disponible sur Google Drive</li>
                  <li>• Rechargez la page</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div className="mt-8 bg-red-50 rounded-xl p-6 border border-red-200">
          <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            ⚠️ Zone de danger
          </h3>
          <p className="text-sm text-red-700 mb-4">
            Cette action supprimera toutes vos données personnalisées et remettra l'application à son état initial. 
            Cette action est <strong>irréversible</strong>.
          </p>
          <p className="text-xs text-red-600 mb-4">
            💡 <strong>Conseil :</strong> Effectuez une sauvegarde avant de réinitialiser.
          </p>
          <Button
            onClick={handleReset}
            variant="danger"
            size="sm"
          >
            <XCircle className="w-4 h-4" />
            Réinitialiser toutes les données
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SyncPage;